import { useState, useEffect, useCallback, useRef } from 'react';
import { KeyBindingManager } from '../../../services/KeyBindingManager';
import { KeyBinding } from '../../../../shared/CharacterTypes';
import { DEFAULT_KEY_BINDINGS } from '../../../../config/input/InputBindings';
import { gameLogger } from '../../../../utils/GameLogger';
import { findBindingConflict } from '../../../systems/input/heldAimInput';
import { formatKeyBinding } from '../../../systems/input/keyLabels';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { EventBus } from '../../../../shared/EventBus';
import { ACTION_METADATA } from '../Settings/keybindingGroups';
import {
  domEventToBindingKey,
  bindingId,
  eventModifier,
  findKeyHolder,
  isModifierCode,
} from '../Settings/keybindingKeymap';
import { keyBindingsNavigationStore } from './keyBindingsNavigationStore';

const actionCategory = (action: string): KeyBinding['category'] =>
  ACTION_METADATA[action]?.category
  ?? DEFAULT_KEY_BINDINGS.bindings.find((b) => b.action === action)?.category
  ?? 'misc';

const actionName = (action: string) => ACTION_METADATA[action]?.label ?? action.replace(/_/g, ' ');

export function readLivePlayerBarIds(): number[] {
  const pm = ServiceRegistry.getInstance().get('EntitySystem', { optional: true })?.getPlayerManager?.();
  const bars = pm?.getPlayer?.()?.actionBar?.layout?.bars;
  if (!bars || bars.length === 0) return [1];
  return bars.map((b) => b.id);
}

export interface CapturingTarget {
  action: string;
  slot: 'primary' | 'secondary';
  targetBinding?: KeyBinding;
}

export function useKeyBindingsCapture(confirmingResetAll: boolean, setConfirmingResetAll: (v: boolean) => void) {
  const [bindings, setBindings] = useState<KeyBinding[]>(() => [
    ...KeyBindingManager.getInstance().getBindings(),
  ]);
  const [liveBarIds, setLiveBarIds] = useState<number[]>(() => readLivePlayerBarIds());
  const [capturingTarget, setCapturingTarget] = useState<CapturingTarget | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);

  useEffect(() => {
    const mgr = KeyBindingManager.getInstance();
    setBindings([...mgr.getBindings()]);
    const unsub = mgr.subscribe(() => {
      setBindings([...mgr.getBindings()]);
    });
    const offHotbar = EventBus.getInstance().on('net:message:hotbarUpdate', (msg) => {
      if (msg.layout?.bars?.length) {
        setLiveBarIds(msg.layout.bars.map((b) => b.id));
      } else {
        setLiveBarIds(readLivePlayerBarIds());
      }
    });
    setLiveBarIds(readLivePlayerBarIds());
    return () => {
      unsub();
      offHotbar();
    };
  }, []);

  useEffect(() => {
    keyBindingsNavigationStore.setEscapeIntercepted(
      capturingTarget !== null || confirmingResetAll,
    );
    return () => {
      keyBindingsNavigationStore.setEscapeIntercepted(false);
    };
  }, [capturingTarget, confirmingResetAll]);

  useEffect(() => {
    if (!confirmingResetAll || capturingTarget) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape' || e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setConfirmingResetAll(false);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [confirmingResetAll, capturingTarget, setConfirmingResetAll]);

  const handleBeginCapture = useCallback(
    (action: string, slot: 'primary' | 'secondary', binding: KeyBinding | null) => {
      setCaptureError(null);
      setCapturingTarget({ action, slot, targetBinding: binding ?? undefined });
    },
    [],
  );

  const handleClearSlot = useCallback(
    (action: string, slot: 'primary' | 'secondary', binding: KeyBinding) => {
      const mgr = KeyBindingManager.getInstance();
      const live = mgr.getBindings();
      const idx = live.findIndex((b) => bindingId(b) === bindingId(binding));
      if (idx !== -1) {
        live.splice(idx, 1);
        mgr.saveBindings();
        setBindings([...mgr.getBindings()]);
        setCaptureError(null);
        gameLogger.info('input', `[KeyBindings] cleared ${action} ${slot} binding (${binding.key})`);
      }
    },
    [],
  );

  const handleResetAction = useCallback((action: string) => {
    const mgr = KeyBindingManager.getInstance();
    const live = mgr.getBindings();
    for (let i = live.length - 1; i >= 0; i--) {
      if (live[i].action === action) {
        live.splice(i, 1);
      }
    }
    const defaultsForAction = DEFAULT_KEY_BINDINGS.bindings.filter((b) => b.action === action);
    for (const def of defaultsForAction) {
      live.push({ ...def });
    }
    mgr.saveBindings();
    setBindings([...mgr.getBindings()]);
    setCapturingTarget(null);
    setCaptureError(null);
    gameLogger.info('input', `[KeyBindings] reset action ${action} to defaults`);
  }, []);

  const commitRebind = useCallback(
    (
      action: string,
      targetBinding: KeyBinding | undefined,
      newKey: string,
      modifier?: KeyBinding['modifier'],
    ) => {
      const mgr = KeyBindingManager.getInstance();
      const live = mgr.getBindings();
      const chord = modifier ? { key: newKey, modifier } : { key: newKey };

      if (targetBinding) {
        const idx = live.findIndex((b) => bindingId(b) === bindingId(targetBinding));
        if (idx !== -1) {
          live[idx] = { action: live[idx].action, category: live[idx].category, ...chord };
        } else {
          live.push({ action, ...chord, category: targetBinding.category ?? actionCategory(action) });
        }
      } else {
        live.push({ action, ...chord, category: actionCategory(action) });
      }

      mgr.saveBindings();
      setBindings([...mgr.getBindings()]);
      gameLogger.info('input', `[KeyBindings] rebound ${action}: -> ${formatKeyBinding(chord)}`);
    },
    [],
  );

  const pendingModifier = useRef<string | null>(null);
  useEffect(() => {
    if (!capturingTarget) return;

    const tryCommit = (newKey: string | null, modifier?: KeyBinding['modifier']) => {
      if (!newKey) {
        setCaptureError('That key is not bindable. Try a letter, digit, arrow, Space, Enter or Tab.');
        return;
      }
      const { action, targetBinding } = capturingTarget;
      const target: KeyBinding = {
        ...(targetBinding ?? { action, key: newKey, category: actionCategory(action) }),
        modifier,
      };
      const freeAimAlt = modifier === 'alt' && action !== 'free_aim'
        ? bindings.find((b) => b.action === 'free_aim' && b.key.toUpperCase() === 'ALT') ?? null
        : null;
      const conflict = findBindingConflict(target, newKey, bindings)
        ?? freeAimAlt
        ?? findKeyHolder(action, newKey, modifier, bindings, DEFAULT_KEY_BINDINGS.bindings);
      if (conflict) {
        setCaptureError(`${formatKeyBinding({ key: newKey, modifier })} conflicts with ${actionName(conflict.action)}.`);
        return;
      }

      commitRebind(action, targetBinding, newKey, modifier);
      setCapturingTarget(null);
      setCaptureError(null);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.code === 'Escape' || e.key === 'Escape') {
        pendingModifier.current = null;
        setCapturingTarget(null);
        setCaptureError(null);
        return;
      }

      if ((e.code === 'Backspace' || e.code === 'Delete') && capturingTarget.targetBinding) {
        handleClearSlot(capturingTarget.action, capturingTarget.slot, capturingTarget.targetBinding);
        setCapturingTarget(null);
        return;
      }

      if (isModifierCode(e.code)) {
        pendingModifier.current = e.code;
        return;
      }
      pendingModifier.current = null;
      tryCommit(domEventToBindingKey(e), eventModifier(e));
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (pendingModifier.current !== e.code) return;
      e.preventDefault();
      e.stopPropagation();
      pendingModifier.current = null;
      tryCommit(domEventToBindingKey(e));
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, [capturingTarget, bindings, commitRebind, handleClearSlot]);

  const handleConfirmResetAll = useCallback(() => {
    const mgr = KeyBindingManager.getInstance();
    mgr.resetToDefaults();
    setBindings([...mgr.getBindings()]);
    setCapturingTarget(null);
    setCaptureError(null);
    setConfirmingResetAll(false);
    gameLogger.info('input', '[KeyBindings] reset all keybindings to defaults');
  }, [setConfirmingResetAll]);

  return {
    bindings,
    liveBarIds,
    capturingTarget,
    captureError,
    handleBeginCapture,
    handleClearSlot,
    handleResetAction,
    handleConfirmResetAll,
  };
}
